<template>
	<div class="pension-calculator-tab">
		<b-alert v-if="readOnly" show variant="primary">
			<strong>View only.</strong> You can see the pension calculator and its report but can't make changes.
		</b-alert>
		<!-- The "Pension being valued" dropdown is rendered inside the calculator's
		     first section (pensionOptionsProp / select-pension). Each pension keeps
		     its own saved valuation, and "Record in agreement" writes into that
		     pension's value in the builder. -->
		<pension-calculator
			:key="calculatorKey"
			:pensionOptionsProp="pensionOptions"
			:selectedPensionIndexProp="selectedPensionIndex"
			:ownerClientIdxProp="ownerClientIdx"
			@select-pension="selectPension"
			:clientsProp="clientsSnapshot"
			:provinceProp="provinceSnapshot"
			:hireDateProp="null"
			:salaryProp="null"
			:cohabitationDateProp="cohabProp"
			:separationDateProp="sepProp"
			:relationshipUnderTwoYearsProp="relationshipUnderTwoYears"
			:initialState="currentInitialState"
			:read-only="readOnly"
			:pensionProp="pensionProp"
			:canRecordProp="canRecord"
			:recordedProp="recordedProp"
			@update-state="onUpdateState"
			@select-result="onSelectResult"
			@record-result="onRecordResult"
		/>
	</div>
</template>

<script>
// Thin host wrapper around the lawyer-supplied pension calculator. It maps the
// matter's party/relationship data onto the calculator's prop contract (see the
// calculator's INTEGRATION.md), keeps one saved valuation per pension on the
// agreement, and records the chosen figure into that pension's value in the
// builder.
//
// Persistence goes through the Agreement page's working copy (`agreementData`),
// exactly as the builder sections do: mutations are picked up by the page's
// debounced whole-document save. The earlier partial-update path
// (updateAgreementFields + refetch) was dropped because its refetch rebuilt the
// working copy and could race with the page's own save when this tab also
// edits a pension entry.
import shortid from 'shortid';
import { cohabitationMonths } from '@/lib/agreement/relationshipFacts';
import { hasEditPermission } from '@/mixins/roleLevelPermissions';
import dialogs from '@/components/ParentingSchedule/dialogs.js';
import toast from '@/mixins/toast';
import PensionCalculator from './PensionCalculator.vue';

// Full province/territory name (as stored on the matter address) → the
// jurisdiction codes the calculator understands.
const PROVINCE_CODES = {
	'british columbia': 'BC',
	alberta: 'AB',
	saskatchewan: 'SK',
	manitoba: 'MB',
	ontario: 'ON',
	quebec: 'QC',
	'new brunswick': 'NB',
	'nova scotia': 'NS',
	'prince edward island': 'PE',
	'newfoundland and labrador': 'NL',
	newfoundland: 'NL',
	yukon: 'YT',
	'northwest territories': 'NT',
	nunavut: 'NU',
};

function provinceCode(name) {
	if (!name) return 'BC';
	return PROVINCE_CODES[String(name).trim().toLowerCase()] || 'BC';
}

// The calculator only offers Female/Male; map from the matter's pronouns.
function toGender(pronouns) {
	const p = (pronouns || '').toLowerCase();
	if (p.startsWith('f') || p.includes('she')) return 'Female';
	if (p.startsWith('m') || p.includes('he')) return 'Male';
	return 'Female';
}

// Stored dates are ISO timestamps ("1981-02-02T08:00:00.000Z"); the calculator
// wants "yyyy-mm-dd". We must derive the LOCAL calendar date, not slice the UTC
// portion: the builder saves dates as a locally-parsed date -> toISOString(), and
// displays them back in local time. Slicing the UTC string shifts the day by one
// for timezones ahead of/behind UTC (e.g. IST turns 1995-01-01 into 1994-12-31).
function toIsoDate(value) {
	if (!value) return '';
	const s = String(value);
	// Already a plain calendar date (no time component) — pass through untouched.
	if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
	const d = new Date(s);
	if (isNaN(d.getTime())) return s.length >= 10 ? s.slice(0, 10) : s;
	const yyyy = d.getFullYear();
	const mm = String(d.getMonth() + 1).padStart(2, '0');
	const dd = String(d.getDate()).padStart(2, '0');
	return `${yyyy}-${mm}-${dd}`;
}

function buildClient(partner, relationship) {
	if (!partner) return null;
	const name = partner.name
		? [partner.name.first, partner.name.last].filter(Boolean).join(' ').trim()
		: '';
	const rel = relationship || {};
	return {
		name,
		dob: toIsoDate(partner.birthdate),
		gender: toGender(partner.pronouns),
		cohabitationDate: toIsoDate(rel.cohabitationDate || rel.marriageDate || ''),
		separationDate: toIsoDate(rel.separationDate || ''),
	};
}

function partnerName(partner) {
	if (!partner || !partner.name) return '';
	return [partner.name.first, partner.name.last].filter(Boolean).join(' ').trim();
}

// A pension is "blank" when nothing meaningful has been typed for its value.
function hasValue(v) {
	return v !== undefined && v !== null && String(v).trim() !== '' && Number(v) !== 0;
}

function formatMoney(n) {
	return Number(n || 0).toLocaleString('en-CA', {
		style: 'currency',
		currency: 'CAD',
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	});
}

export default {
	name: 'PensionCalculatorTab',
	components: { PensionCalculator },
	mixins: [dialogs, toast],
	props: {
		agreementData: { type: Object, required: true },
	},
	computed: {
		pensions() {
			return (this.agreementData && this.agreementData.pensions) || [];
		},
		// Saved valuations keyed by pension id: { [pensionId]: calculatorState + recorded }.
		valuations() {
			return (this.agreementData && this.agreementData.pensionValuations) || {};
		},
		pensionOptions() {
			return this.pensions.map((p, index) => ({
				value: index,
				text: `${p.name || `Pension ${index + 1}`}${this.ownerNameFor(p) ? ` (${this.ownerNameFor(p)})` : ''}`,
			}));
		},
		// The chosen pension's owner as an index into the calculator's client list,
		// so a fresh valuation opens on the right person.
		ownerClientIdx() {
			const owner = this.ownerNameFor(this.selectedPension);
			if (!owner) return null;
			const idx = this.clientsSnapshot.findIndex(
				(c) => c.name && c.name.trim().toLowerCase() === owner.trim().toLowerCase()
			);
			return idx === -1 ? null : idx;
		},
		selectedPension() {
			return this.selectedPensionIndex === null ? null : this.pensions[this.selectedPensionIndex] || null;
		},
		selectedPensionId() {
			return this.selectedPension ? this.selectedPension.id || null : null;
		},
		// Remount the calculator when the pension changes so its inputs reload
		// from that pension's saved state.
		calculatorKey() {
			return this.selectedPensionId ? `pension-${this.selectedPensionId}` : 'pension-none';
		},
		currentInitialState() {
			if (this.selectedPensionId && this.valuations[this.selectedPensionId]) {
				// eslint-disable-next-line no-unused-vars
				const { recorded, ...state } = this.valuations[this.selectedPensionId];
				return state;
			}
			// No pension chosen (or none valued yet): show the legacy single state, if any.
			return this.legacyState || undefined;
		},
		pensionProp() {
			const p = this.selectedPension;
			if (!p || !this.selectedPensionId) return null;
			return {
				id: this.selectedPensionId,
				name: p.name || '',
				ownerName: this.ownerNameFor(p),
				value: p.value,
				orderOfTax: p.orderOfTax || null,
			};
		},
		recordedProp() {
			const v = this.selectedPensionId ? this.valuations[this.selectedPensionId] : null;
			return (v && v.recorded) || null;
		},
		canRecord() {
			return !this.readOnly && hasEditPermission('section-property');
		},
		// Relationship dates stay live (reactive), not snapshotted, so the
		// calculator — where they are read-only — always shows the builder's
		// current values. The builder owns these dates.
		cohabProp() {
			const r = (this.agreementData && this.agreementData.relationship) || {};
			return toIsoDate(r.cohabitationDate || r.marriageDate || '');
		},
		sepProp() {
			const r = (this.agreementData && this.agreementData.relationship) || {};
			return toIsoDate(r.separationDate || '');
		},
		// Verbatim builder relationship status — the calculator uses it to flag the
		// "under two years with children" case where a valuation is not applicable.
		readOnly() {
			return !hasEditPermission('pension-calculator');
		},
		relationshipUnderTwoYears() {
			const r = (this.agreementData && this.agreementData.relationship) || {};
			if (!r.cohabitationDate || !r.separationDate) {
				return false;
			}
			return cohabitationMonths(r) < 24;
		},
	},
	data() {
		// Snapshot the party inputs ONCE at creation so a save mid-edit cannot flow
		// back in as new props and reset the calculator. The tab is rendered behind
		// a v-if, so it is recreated (and re-snapshotted) each time it is opened.
		const ad = this.agreementData || {};
		const rel = ad.relationship || {};
		const clients = [buildClient(ad.partner1, rel), buildClient(ad.partner2, rel)].filter(
			(c) => c && (c.name || c.dob)
		);
		const seedProvince =
			(ad.partner1 && ad.partner1.address && ad.partner1.address.province) ||
			(ad.partner2 && ad.partner2.address && ad.partner2.address.province) ||
			'';
		return {
			clientsSnapshot: clients,
			provinceSnapshot: provinceCode(seedProvince),
			// The pre-per-pension single saved state. It seeds the first pension
			// that gets valued, so nothing already entered is lost. Left in place
			// on the document; it is simply no longer written.
			legacyState: ad.pensionCalculatorState || null,
			selectedPensionIndex: null,
			lastResult: null,
		};
	},
	created() {
		// Reopen on a pension that already has a valuation; otherwise, with a
		// single pension on the matter, pick it so the lawyer's work is saved.
		const withValuation = this.pensions.findIndex((p) => p.id && this.valuations[p.id]);
		if (withValuation !== -1) {
			this.selectPension(withValuation);
		} else if (this.pensions.length === 1) {
			this.selectPension(0);
		}
	},
	methods: {
		ownerNameFor(pension) {
			if (!pension) return '';
			const ad = this.agreementData || {};
			if (pension.ownership === '0' || pension.ownership === 0) return partnerName(ad.partner1);
			if (pension.ownership === '1' || pension.ownership === 1) return partnerName(ad.partner2);
			return pension.ownership ? 'Joint' : '';
		},
		selectPension(index) {
			const idx = index === null || index === undefined || index === '' ? null : Number(index);
			if (idx === null || !this.pensions[idx]) {
				this.selectedPensionIndex = null;
				return;
			}
			const pension = this.pensions[idx];
			if (this.readOnly) {
				this.selectedPensionIndex = idx;
				return;
			}
			// Pensions have no identifier of their own; give one the first time it
			// is linked so the valuation survives reordering or renaming.
			if (!pension.id) this.$set(pension, 'id', shortid.generate());
			if (!this.valuations[pension.id] && this.legacyState) {
				this.writeValuation(pension.id, { ...this.legacyState, recorded: null });
				this.legacyState = null;
			}
			this.selectedPensionIndex = idx;
		},
		writeValuation(pensionId, entry) {
			const next = { ...this.valuations, [pensionId]: entry };
			this.$set(this.agreementData, 'pensionValuations', next);
		},
		onUpdateState(state) {
			if (this.readOnly) return;
			const id = this.selectedPensionId;
			if (!id) return; // nothing to save against until a pension is chosen
			const existing = this.valuations[id] || {};
			const entry = { ...state, recorded: existing.recorded || null };
			// The calculator emits on load with the restored state; skip no-op writes
			// so the page's save is not triggered for nothing.
			if (JSON.stringify(entry) === JSON.stringify(existing)) return;
			this.writeValuation(id, entry);
		},
		onSelectResult(payload) {
			// The chosen scenario's figures; recording is the explicit action below.
			this.lastResult = payload;
		},
		async onRecordResult({ pensionId, basis, orderOfTax, amount, scenario }) {
			if (!this.canRecord) return;
			const pension = this.pensions.find((p) => p.id === pensionId);
			if (!pension) {
				this.errorMessage('Pension not found', 'Choose the pension being valued and try again.');
				return;
			}
			const label = basis === 'pre' ? 'pre-tax' : 'post-tax';
			if (hasValue(pension.value) && Number(pension.value) !== amount) {
				const ok = await this.$confirmDialog(
					`${pension.name || 'This pension'} already has a value of ${formatMoney(pension.value)}` +
						`${pension.orderOfTax ? ` (${pension.orderOfTax})` : ''} in the builder.\n\n` +
						`Replace it with the ${label} figure of ${formatMoney(amount)}?`,
					{ title: 'Replace the pension value?', okTitle: 'Replace', cancelTitle: 'Keep existing' }
				);
				if (!ok) return;
			}
			this.$set(pension, 'value', amount);
			this.$set(pension, 'orderOfTax', orderOfTax);
			if (!pension.partiesAssignValue) this.$set(pension, 'partiesAssignValue', true);
			const existing = this.valuations[pensionId] || {};
			this.writeValuation(pensionId, {
				...existing,
				recorded: {
					amount,
					basis,
					scenario: scenario || '',
					pensionName: pension.name || '',
					at: new Date().toISOString(),
				},
			});
			this.successMessage(
				'Pension value recorded',
				`${pension.name || 'Pension'}: ${formatMoney(amount)} (${label}). You can still edit it in the Pensions section.`
			);
		},
	},
};
</script>

<style scoped>
.pension-calculator-tab {
	width: 100%;
}
</style>
